/**
 * MoviePosterScanner — take/upload a movie poster photo, send to Gemini 3
 * Flash via /api/film-ia/scan-poster and display the detected film's TMDB
 * fiche. Users can then re-ask Déesse about that movie.
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Camera, Upload, Loader2, Sparkles, Film, Star, Clock, Calendar, User, Play, RotateCcw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const MoviePosterScanner = ({ onClose, onAskMyIA }) => {
  const { t } = useLanguage();
  const [preview, setPreview] = useState(null); // dataURL for the captured/uploaded image
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { detected, title, tmdb, ... }
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp|heic|heif)$/i.test(file.type)) {
      toast.error(t('posterScanner.invalidFormat', 'Format non supporté (JPG, PNG, WebP uniquement)'));
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error(t('posterScanner.tooLarge', 'Image trop grande (max 8 Mo)'));
      return;
    }
    // Preview
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);

    // Upload
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post('/api/film-ia/scan-poster', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(r.data);
      if (!r.data?.detected) {
        toast.error(t('posterScanner.notDetected', 'Aucun film détecté sur cette affiche'));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t('posterScanner.error', 'Erreur d\'analyse'));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setPreview(null);
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const askMyIA = () => {
    const t = result?.tmdb;
    const query = t
      ? `Parle-moi du film « ${t.title}${t.release_date ? ` (${(t.release_date || '').slice(0,4)})` : ''} »`
      : `Parle-moi du film « ${result?.title} »`;
    onAskMyIA?.(query);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="poster-scanner">
      <div className="my-6 max-w-lg w-full bg-gray-900 rounded-3xl border border-white/10 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-fuchsia-400" /> {t('posterScanner.title', 'Scanner une affiche de film')}</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10" data-testid="poster-scanner-close"><X className="w-5 h-5" /></button>
        </div>

        {/* Preview */}
        {preview ? (
          <div className="relative rounded-2xl overflow-hidden mb-4 bg-black">
            <img src={preview} alt="Affiche capturée" className="w-full max-h-80 object-contain" data-testid="poster-scanner-preview" />
            {busy && (
              <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center text-fuchsia-200">
                <Loader2 className="w-8 h-8 animate-spin mb-2" />
                <p className="text-sm">{t('posterScanner.analyzing', 'Analyse en cours…')}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-2xl border-2 border-dashed border-white/15 p-6 text-center mb-4">
            <Camera className="w-10 h-10 mx-auto mb-3 text-fuchsia-400 opacity-70" />
            <p className="text-sm text-white/70 mb-1">{t('posterScanner.subtitle', 'Prenez en photo l\'affiche d\'un film au cinéma')}</p>
            <p className="text-xs text-white/40">{t('posterScanner.hint', 'Destine IA identifie le titre et vous présente le film.')}</p>
          </div>
        )}

        {/* Buttons */}
        {!result && (
          <div className="grid grid-cols-2 gap-2 mb-4">
            <label className="cursor-pointer">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={e => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="poster-scanner-camera-input"
              />
              <div className="py-2.5 rounded-xl bg-fuchsia-500 hover:bg-fuchsia-600 text-white text-sm font-semibold flex items-center justify-center gap-1.5">
                <Camera className="w-4 h-4" /> {t('posterScanner.takePhoto', 'Prendre en photo')}
              </div>
            </label>
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={e => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="poster-scanner-upload-input"
              />
              <div className="py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-semibold flex items-center justify-center gap-1.5">
                <Upload className="w-4 h-4" /> {t('posterScanner.upload', 'Importer')}
              </div>
            </label>
          </div>
        )}

        {/* Result — TMDB fiche */}
        {result && result.detected && (
          <div className="rounded-2xl bg-gradient-to-br from-fuchsia-500/10 to-purple-500/10 border border-fuchsia-400/25 p-4 mb-4" data-testid="poster-scanner-result">
            <div className="flex gap-3">
              {result.tmdb?.poster_path && (
                <img src={result.tmdb.poster_path} alt={result.tmdb?.title || result.title} className="w-24 rounded-lg" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wide text-fuchsia-300 mb-0.5">
                  {t('posterScanner.detected', 'Détecté · confiance :')} {result.confidence}
                </div>
                <h4 className="text-lg font-bold text-white" data-testid="poster-scanner-title">
                  {result.tmdb?.title || result.title}
                </h4>
                {result.tmdb?.original_title && result.tmdb.original_title !== result.tmdb.title && (
                  <div className="text-xs text-white/60 italic">{result.tmdb.original_title}</div>
                )}
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-white/60 mt-1.5">
                  {result.tmdb?.release_date && (
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {result.tmdb.release_date.slice(0, 4)}</span>
                  )}
                  {result.tmdb?.runtime && (
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {result.tmdb.runtime} min</span>
                  )}
                  {typeof result.tmdb?.vote_average === 'number' && (
                    <span className="inline-flex items-center gap-1"><Star className="w-3 h-3 text-yellow-400" /> {result.tmdb.vote_average.toFixed(1)}</span>
                  )}
                  {result.tmdb?.director && (
                    <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {result.tmdb.director}</span>
                  )}
                </div>
                {result.tmdb?.genres?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {result.tmdb.genres.slice(0, 4).map(g => (
                      <span key={g} className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/70">{g}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {result.tmdb?.overview && (
              <p className="mt-3 text-sm text-white/80 line-clamp-5">{result.tmdb.overview}</p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              {result.tmdb?.trailer_key && (
                <a href={`https://www.youtube.com/watch?v=${result.tmdb.trailer_key}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 text-xs inline-flex items-center gap-1" data-testid="poster-scanner-trailer">
                  <Play className="w-3.5 h-3.5" /> {t('posterScanner.trailer', 'Bande-annonce')}
                </a>
              )}
              <button onClick={askMyIA} className="px-3 py-1.5 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-600 text-white text-xs inline-flex items-center gap-1 font-semibold" data-testid="poster-scanner-ask-ia">
                <Sparkles className="w-3.5 h-3.5" /> {t('posterScanner.askMyIA', 'Demander à Déesse')}
              </button>
              <button onClick={reset} className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs inline-flex items-center gap-1" data-testid="poster-scanner-reset">
                <RotateCcw className="w-3.5 h-3.5" /> {t('posterScanner.scanAnother', 'Scanner une autre')}
              </button>
            </div>
          </div>
        )}

        {result && !result.detected && (
          <div className="rounded-xl bg-red-500/10 border border-red-400/30 p-3 text-sm text-red-200 mb-4">
            <Film className="w-4 h-4 inline mr-1" />
            {t('posterScanner.notDetected', "L'IA n'a pas pu identifier de film sur cette image. Réessayez avec une photo plus nette.")}
            <button onClick={reset} className="ml-2 underline">{t('posterScanner.retry', 'Réessayer')}</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MoviePosterScanner;
